import React from "react";
import {
  Box,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
  Button,
} from "@material-ui/core";
import PollIcon from "@material-ui/icons/Poll";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import { i18n } from "../../translate/i18n";

export const POLL_MAX_OPTIONS = 12;

export const parsePollOptions = (raw) => {
  if (Array.isArray(raw)) return raw.map((o) => String(o ?? ""));
  if (!raw) return ["", ""];
  try {
    const list = JSON.parse(raw);
    return Array.isArray(list) && list.length ? list.map((o) => String(o ?? "")) : ["", ""];
  } catch {
    return String(raw).split(/\r?\n/);
  }
};

const t = (key) => i18n.t(`campaigns.dialog.poll.${key}`);

const CampaignPollEditor = ({ values, setFieldValue, disabled }) => {
  const enabled = !!values.pollEnabled;
  const options = parsePollOptions(values.pollOptions);

  const setOptions = (next) => setFieldValue("pollOptions", next);

  return (
    <Box mt={3} p={2} border={1} borderColor="divider" borderRadius={12}>
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={enabled}
            disabled={disabled}
            onChange={(e) => {
              setFieldValue("pollEnabled", e.target.checked);
              if (e.target.checked && !Array.isArray(values.pollOptions)) setOptions(options);
            }}
          />
        }
        label={
          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
            <PollIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2">{t("toggle")}</Typography>
          </Box>
        }
      />
      <Typography variant="caption" color="textSecondary" component="div">
        {t("hint")}
      </Typography>

      {enabled && (
        <Box mt={2}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label={t("question")}
            value={values.pollQuestion || ""}
            disabled={disabled}
            inputProps={{ maxLength: 255 }}
            helperText={t("questionHint")}
            onChange={(e) => setFieldValue("pollQuestion", e.target.value)}
          />
          <Box mt={2}>
            {options.map((opt, idx) => (
              <Box key={idx} display="flex" alignItems="center" mb={1} style={{ gap: 6 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={`${t("option")} ${idx + 1}`}
                  value={opt}
                  disabled={disabled}
                  inputProps={{ maxLength: 100 }}
                  onChange={(e) => {
                    const next = [...options];
                    next[idx] = e.target.value;
                    setOptions(next);
                  }}
                />
                <IconButton
                  size="small"
                  disabled={disabled || options.length <= 2}
                  onClick={() => setOptions(options.filter((_, i) => i !== idx))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              color="primary"
              startIcon={<AddIcon />}
              disabled={disabled || options.length >= POLL_MAX_OPTIONS}
              onClick={() => setOptions([...options, ""])}
            >
              {t("addOption")} ({options.length}/{POLL_MAX_OPTIONS})
            </Button>
          </Box>
          <FormControlLabel
            style={{ marginTop: 8 }}
            control={
              <Switch
                color="primary"
                checked={Number(values.pollSelectableCount) === 0}
                disabled={disabled}
                onChange={(e) => setFieldValue("pollSelectableCount", e.target.checked ? 0 : 1)}
              />
            }
            label={<Typography variant="body2">{t("multiple")}</Typography>}
          />
        </Box>
      )}
    </Box>
  );
};

export default CampaignPollEditor;
